import { useState, useCallback } from 'react';
import styled from 'styled-components';

import { useVideoProject } from '@contexts/VideoProject.context';
import View from '@components/layout/views/View';
import Loader from '@components/loading/Loader';
import ProjectSidebar from './components/sidebar/ProjectSidebar';
import ResultsPanel from './components/results/ResultsPanel';
import { fetchUrl } from '@utils/api';
import { downloadFile } from '@utils/files';
import { useToast } from '@contexts/Toast.context';

const SplitLayout = styled.div`
	display: grid;
	grid-template-columns: ${({ $collapsed }) => ($collapsed ? '44px' : '320px')} 1fr;
	height: ${({ theme }) => theme.utils.fullHeight};
	transition: grid-template-columns 0.35s ease;
`;

const ErrorContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 400px;
	text-align: center;
	gap: 1rem;
`;

const Title = styled.h2`
	color: ${({ theme }) => theme.colors.green};
	margin: 0;
`;

const Message = styled.p`
	color: rgba(255, 255, 255, 0.8);
	font-size: 1.125rem;
`;

const VideoProjectView = () => {
	const { project, isLoading, error, videoData, resultsData, analysisData } = useVideoProject();
	const { addToast } = useToast();

	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

	const toggleSidebar = useCallback(() => {
		setSidebarCollapsed(prev => !prev);
	}, []);

	const handleSetPeriod = useCallback(({ start, end }) => {
		// TODO: wire to API / filter results
		console.log('Set period:', start, end);
	}, []);

	const handleExport = useCallback(async () => {
		const reportPath = analysisData?.result_paths?.report_pdf;
		if (!reportPath) return;
		try {
			const data = await fetchUrl(`filelink?key=${reportPath}`);
			if (!data?.url) return;
			downloadFile({ url: data.url, title: 'detection_report', fileExtension: 'pdf' });
		} catch (err) {
			console.error('Export failed:', err);
			addToast({
				id: 'export-pdf-error',
				bg: 'danger',
				title: 'Export failed',
				message: 'Could not download the detection report. Please try again.',
			});
		}
	}, [analysisData?.result_paths?.report_pdf, addToast]);

	const handleReprocess = useCallback(() => {
		// TODO: wire to reprocessVideo API
		console.log('Reprocess');
	}, []);

	const handleStartDetection = useCallback(() => {
		// TODO: wire to startVideoProcessing API
		console.log('Start detection');
	}, []);

	const handleExportCsv = useCallback(() => {
		// TODO: wire to CSV export
		console.log('Export CSV');
	}, []);

	if (isLoading) {
		return (
			<View title="Video Project">
				<Loader />
			</View>
		);
	}

	if (error) {
		return (
			<View title="Video Project">
				<ErrorContainer>
					<Title>Error Loading Project</Title>
					<Message className="text-danger">
						{error.message || 'Failed to load video project'}
					</Message>
				</ErrorContainer>
			</View>
		);
	}

	const jobStatus = resultsData?.job_status;
	const jobProgress = resultsData?.progress;
	const detections = resultsData?.detections || [];
	const videoUrl = analysisData?.result_paths?.masked_video || '';

	return (
		<SplitLayout $collapsed={sidebarCollapsed}>
			<ProjectSidebar
				isCollapsed={sidebarCollapsed}
				onToggle={toggleSidebar}
				project={project}
				video={videoData}
				job={resultsData}
				analysis={analysisData}
			/>
			<ResultsPanel
				jobStatus={jobStatus}
				jobProgress={jobProgress}
				videoUrl={videoUrl}
				detections={detections}
				results={resultsData}
				analysis={analysisData}
				onExport={handleExport}
				onReprocess={handleReprocess}
				onStartDetection={handleStartDetection}
				onSetPeriod={handleSetPeriod}
				onExportCsv={handleExportCsv}
			/>
		</SplitLayout>
	);
};

export default VideoProjectView;
